import React, { useEffect, useState } from "react";
import Select from 'react-select';
import { isEmpty, without } from "lodash";

import { useReactFlow } from "reactflow";
import { useTranslation } from "react-i18next";
import TextWithVariables from "./TextWithVariables";
import OptionsSublist from "./OptionsSublist";
import AdvancedActions from "./AdvancedActions";
import InactivityTimeoutConfig from "./InactivityTimeoutConfig";

import selectStyles from '../../../util/selectStyles';
import FailedOption from "./FailedOption";
import { uuid, getOptionCollection } from "./utils";
import { CHATBOT_OPTIONS_TYPE } from '../../../constants/AppConstants';
import IconMC from '../../../presentation/components/IconMC';

const buildLabelTypeOption = (type) => {
  let icon;
  let text;
  let message;

  switch (type) {
    case 'text':
      icon = 'text-outline'; text = 'Texto libre'; message = 'Permitir una respuesta de texto libre';
      break;
    case 'anything':
      icon = 'message-square-outline'; text = 'Cualquier mensaje'; message = 'Permitir cualquier tipo de mensaje: texto, multimedia, etc.';
      break;
    case 'unique':
      icon = 'checkmark-outline'; text = 'Opción única'; message = 'Permitir escoger una sola opción de una lista';
      break;
    case 'location':
      icon = 'pin-outline'; text = 'Ubicación'; message = 'Permitir recibir la ubicación del usuario y guardar su latitud y longitud';
      break;
    default:
      icon = ''; text = ''; message = '';
  }

  return (
    <div className="d-flex flex-row">
      <div className="align-self-center mr-10">
        <IconMC name={icon} size="base" />
      </div>
      <div className="d-flex flex-column align-self-center">
        <span className="fz-12 font-700">{text}</span>
        <span className="fz-11">{message}</span>
      </div>
    </div>
  );
};

const answerTypeOptions = [
  { value: 'text', label: buildLabelTypeOption('text') },
  { value: 'anything', label: buildLabelTypeOption('anything') },
  { value: 'unique', label: buildLabelTypeOption('unique') },
  { value: 'location', label: buildLabelTypeOption('location') }
];

const QuestionStep = ({
  chatbotOption,
  setChatbotOption,
  errors,
  customerColumns,
  chatbot
}) => {
  const { getNode } = useReactFlow();
  const [apiVariables, setApiVariables] = useState([]);

  const maxCharacters = () => {
    if (chatbotOption.interactive === 'text') return 400;
    if (chatbotOption.interactive === 'list') return 24;
    return 20;
  };

  const updateInfo = (name, value) => {
    setChatbotOption({
      ...chatbotOption,
      [name]: value
    });
  };

  const handleSelect = (option) => {
    if (option.value === 'unique' && isEmpty(getOptionCollection(chatbotOption, 'option_sub_lists'))) {
      addNewOption();
    }

    updateInfo('answer_type', option.value);
  };

  const addNewOption = () => {
    const newArray = getOptionCollection(chatbotOption, 'option_sub_lists').concat({
      id: null,
      value_to_show: '',
      list_item_description: '',
      _destroy: false
    });

    updateInfo('option_sub_lists', newArray);
  };

  const addFailedOption = (currentOptions) => {
    const nodeId = uuid();
    const failedNodeId = uuid();

    if (currentOptions.length === 0) {
      return currentOptions.concat({
        id: null,
        text: 'Respuesta',
        option_deleted: false,
        node_id: nodeId,
        source: chatbotOption.node_id,
        target: nodeId,
        parent_id: chatbotOption.id,
        fake_option: true
      })
        .concat({
          id: null,
          text: 'Intentos fallidos',
          option_deleted: false,
          node_id: failedNodeId,
          source: chatbotOption.node_id,
          target: failedNodeId,
          parent_id: chatbotOption.id,
          fake_option: true,
          option_type: 'failure'
        });
    }
    return currentOptions.map((item) => ({
      ...item,
      option_deleted: false
    }));
  };

  const removeFailedOption = (currentOptions) => {
    let newArray = [...currentOptions];

    currentOptions.forEach((fakeOption) => {
      if (fakeOption.fake_option) {
        if (fakeOption?.id) {
          const optIndex = currentOptions.lastIndexOf(fakeOption);
          newArray[optIndex].option_deleted = true;
        } else {
          newArray = without(currentOptions, fakeOption);
        }
      }
    });

    return newArray;
  };

  useEffect(() => {
    if (chatbotOption?.source) {
      const sourceNode = getNode(chatbotOption.source);
      if (sourceNode.source) {
        const source = getNode(sourceNode.source).data;
        if (source?.option?.option_type === CHATBOT_OPTIONS_TYPE.API && source?.option?.api_options) {
          const newApiVariables = source.option.api_options?.variables || [];
          setApiVariables(newApiVariables);
        }
      }
    }
  }, [chatbotOption?.source]);

  return (
    <>
      <TextWithVariables
        chatbotOption={chatbotOption}
        setChatbotOption={setChatbotOption}
        errors={errors}
        customerColumns={customerColumns}
        maxChars={chatbot.max_button_size_text}
      />

      <div className="col-md-12 mt-20">
        <div className="figma-field mb-0">
          <label>Tipo de respuesta</label>
          {errors.answer_type && (<span className="ml-10 error-msg fz-12">{errors.answer_type}</span>)}
          <Select
            options={answerTypeOptions}
            placeholder="Selecciona"
            className={chatbotOption.answer_type ? '' : 'select-tag'}
            classNamePrefix={chatbotOption.answer_type ? '' : 'select-tag'}
            styles={selectStyles}
            onChange={handleSelect}
            value={[answerTypeOptions.find((el) => el.value === chatbotOption.answer_type)]}
            name="answer_type"
            components={{
              IndicatorSeparator: () => null
            }}
          />
        </div>
      </div>

      <OptionsSublist
        chatbotOption={chatbotOption}
        setChatbotOption={setChatbotOption}
        errors={errors}
        maxCharacters={maxCharacters}
        apiVariables={apiVariables}
        chatbot={chatbot}
      />

      { (chatbotOption.answer_type === 'unique' || chatbotOption.answer_type === 'location') && (
        <div className="mt-20">
          <FailedOption
            chatbotOption={chatbotOption}
            setChatbotOption={setChatbotOption}
            maxCharacters={maxCharacters}
            errors={errors}
            addFailedOption={addFailedOption}
            removeFailedOption={removeFailedOption}
          />
        </div>
      )}
      <div className="mt-20">
        <AdvancedActions
          chatbot={chatbot}
          chatbotOption={chatbotOption}
          setChatbotOption={setChatbotOption}
          customerColumns={customerColumns}
          errors={errors}
        />
      </div>

      <div className="mt-20">
        <InactivityTimeoutConfig
          chatbotOption={chatbotOption}
          setChatbotOption={setChatbotOption}
          errors={errors}
        />
      </div>
    </>
  );
};

export default QuestionStep;
